import { Form, Select, Flex } from "antd";
import { USER_ROLE_OPTIONS, UserRole } from "helpers/constants/users";
import { VLInput } from "views/uikit/FormFields/VLInput/VLInput";
import { VLInputWithPassword } from "views/uikit/FormFields/VLInputWithPassword";
import styles from "./style.module.scss";

const CREATE_USER_ROLE_OPTIONS = USER_ROLE_OPTIONS.filter(
  (option) => option.value !== UserRole.Admin,
);

export type FormValues = {
  username: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  password: string;
  confirmPassword: string;
};

const FORM_ID = "create-user-form";

interface UserFormProps {
  form: any;
  initialValues?: Partial<FormValues>;
  onFinish: (values: FormValues) => Promise<void>;
  loading?: boolean;
}

export const UserForm = ({
  form,
  initialValues = {},
  onFinish,
  loading = false,
}: UserFormProps) => {
  return (
    <Form
      form={form}
      id={FORM_ID}
      layout="vertical"
      initialValues={{
        username: "",
        firstName: "",
        lastName: "",
        role: UserRole.Viewer,
        password: "",
        confirmPassword: "",
        ...initialValues,
      }}
      onFinish={onFinish}
      disabled={loading}
    >
      <Flex vertical gap={16} style={{ marginTop: 15 }}>
        <Form.Item
          name="username"
          label="Username"
          rules={[
            { required: true, message: "Username is required" },
            { whitespace: true, message: "Username cannot be empty" },
            {
              validator: (_, value) =>
                value && /\s/.test(value)
                  ? Promise.reject(
                      new Error("Username must not contain spaces"),
                    )
                  : Promise.resolve(),
            },
          ]}
        >
          <VLInput />
        </Form.Item>
        <Form.Item
          name="firstName"
          label="First Name"
          rules={[
            { required: true, message: "First name is required" },
            { whitespace: true, message: "First name cannot be blank" },
          ]}
        >
          <VLInput />
        </Form.Item>

        <Form.Item
          name="lastName"
          label="Last Name"
          rules={[
            { required: true, message: "Last name is required" },
            { whitespace: true, message: "Last name cannot be blank" },
          ]}
        >
          <VLInput />
        </Form.Item>

        <Form.Item
          name="role"
          label="Role"
          rules={[{ required: true, message: "Role is required" }]}
        >
          <Select
            options={CREATE_USER_ROLE_OPTIONS}
            placeholder="Select role"
            getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[
            { required: true, message: "Password is required" },
            { min: 8, message: "Password must be at least 8 characters" },
          ]}
        >
          <VLInputWithPassword className={styles.inputWithPassword} />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Confirm Password"
          dependencies={["password"]}
          rules={[
            {
              required: true,
              message: "Please confirm your password",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Passwords do not match"));
              },
            }),
          ]}
        >
          <VLInputWithPassword className={styles.inputWithPassword} />
        </Form.Item>
      </Flex>
    </Form>
  );
};
